import React, { useMemo } from 'react'
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Text,
  Box,
  Spinner,
  Progress,
  Badge,
  Flex,
  StatGroup,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Divider,
  Heading,
  Grid,
  GridItem,
  Tooltip,
} from '@chakra-ui/react'
import { usePedidoSpot } from '../hooks/PedidoSpotContext'

export const ModalSumarioPedidosSpot = ({ isOpen, onClose }) => {
  const { pedidos, isLoading, error } = usePedidoSpot()

  const estatisticas = useMemo(() => {
    if (!pedidos || !pedidos.length) return null

    // Calcular valor total de todos os pedidos
    const valorTotal = pedidos.reduce((acc, pedido) => acc + (typeof pedido.valor_total === 'number' ? pedido.valor_total : 0), 0)

    // Agrupar por status
    const porStatus = {}
    pedidos.forEach((pedido) => {
      const status = pedido.status || 'Não informado'
      if (!porStatus[status]) {
        porStatus[status] = {
          quantidade: 0,
          valor: 0,
        }
      }
      porStatus[status].quantidade += 1
      porStatus[status].valor += typeof pedido.valor_total === 'number' ? pedido.valor_total : 0
    })

    // Converter para array e ordenar por quantidade
    const statusArray = Object.entries(porStatus).map(([status, dados]) => ({
      status,
      ...dados,
      percentualQuantidade: (dados.quantidade / pedidos.length) * 100,
      percentualValor: (dados.valor / valorTotal) * 100,
    }))

    statusArray.sort((a, b) => b.quantidade - a.quantidade)

    // Agrupar por fornecedor
    const porFornecedor = {}
    pedidos.forEach((pedido) => {
      const fornecedor = pedido.fornecedor || 'Não informado'
      if (!porFornecedor[fornecedor]) {
        porFornecedor[fornecedor] = {
          quantidade: 0,
          valor: 0,
          statusContagem: {},
        }
      }
      porFornecedor[fornecedor].quantidade += 1
      porFornecedor[fornecedor].valor += typeof pedido.valor_total === 'number' ? pedido.valor_total : 0

      // Contar status por fornecedor
      const status = pedido.status || 'Não informado'
      if (!porFornecedor[fornecedor].statusContagem[status]) {
        porFornecedor[fornecedor].statusContagem[status] = 0
      }
      porFornecedor[fornecedor].statusContagem[status] += 1
    })

    // Converter para array e ordenar por quantidade
    const fornecedoresArray = Object.entries(porFornecedor).map(([fornecedor, dados]) => ({
      fornecedor,
      ...dados,
      percentualQuantidade: (dados.quantidade / pedidos.length) * 100,
      percentualValor: (dados.valor / valorTotal) * 100,
    }))

    fornecedoresArray.sort((a, b) => b.quantidade - a.quantidade)

    // Totais gerais de itens
    const totalItens = pedidos.reduce((acc, pedido) => {
      return acc + (pedido.itens?.length || 0)
    }, 0)

    // Contagens de transportadoras
    const porTransportadora = {}
    pedidos.forEach((pedido) => {
      const transportadora = pedido.transportadora || 'Não informado'
      if (!porTransportadora[transportadora]) {
        porTransportadora[transportadora] = 0
      }
      porTransportadora[transportadora] += 1
    })

    // Converter transportadoras para array e ordenar
    const transportadorasArray = Object.entries(porTransportadora).map(([transportadora, quantidade]) => ({
      transportadora,
      quantidade,
      percentual: (quantidade / pedidos.length) * 100,
    }))

    transportadorasArray.sort((a, b) => b.quantidade - a.quantidade)

    return {
      totalPedidos: pedidos.length,
      valorTotal,
      mediaValorPorPedido: valorTotal / pedidos.length,
      totalItens,
      mediaItensPorPedido: totalItens / pedidos.length,
      porStatus: statusArray,
      porFornecedor: fornecedoresArray,
      porTransportadora: transportadorasArray,
    }
  }, [pedidos])

  if (isLoading) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Sumário de Pedidos SPOT</ModalHeader>
          <ModalCloseButton />
          <ModalBody textAlign="center" py={10}>
            <Spinner size="xl" color="blue.500" />
            <Text mt={4}>Carregando sumário de pedidos...</Text>
          </ModalBody>
        </ModalContent>
      </Modal>
    )
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="3xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Sumário de Pedidos SPOT</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {error ? (
            <Box textAlign="center" py={4}>
              <Text color="red.500">Erro ao carregar o sumário. Por favor, tente novamente.</Text>
            </Box>
          ) : estatisticas ? (
            <>
              <Grid templateColumns="repeat(2, 1fr)" gap={4} mb={6}>
                <GridItem colSpan={2}>
                  <Heading size="sm" mb={2}>
                    Visão Geral de Pedidos
                  </Heading>
                </GridItem>

                <Box p={3} borderWidth="1px" borderRadius="lg" bg="blue.50">
                  <Text fontWeight="bold" mb={2}>
                    Total de Pedidos no Período
                  </Text>
                  <StatGroup>
                    <Stat>
                      <StatNumber>{estatisticas.totalPedidos}</StatNumber>
                      <StatLabel>Pedidos</StatLabel>
                    </Stat>
                    <Stat>
                      <StatNumber>{estatisticas.totalItens}</StatNumber>
                      <StatLabel>Itens</StatLabel>
                      <StatHelpText>Média de {estatisticas.mediaItensPorPedido.toFixed(1)} itens por pedido</StatHelpText>
                    </Stat>
                  </StatGroup>
                </Box>

                <Box p={3} borderWidth="1px" borderRadius="lg" bg="purple.50">
                  <Text fontWeight="bold" mb={2}>
                    Valores Totais
                  </Text>
                  <StatGroup>
                    <Stat>
                      <StatNumber>{estatisticas.valorTotal.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</StatNumber>
                      <StatLabel>Valor Total</StatLabel>
                    </Stat>
                    <Stat>
                      <StatNumber>{estatisticas.mediaValorPorPedido.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</StatNumber>
                      <StatLabel>Média por Pedido</StatLabel>
                    </Stat>
                  </StatGroup>
                </Box>
              </Grid>

              <Divider my={4} />

              <Heading size="sm" mb={2}>
                Pedidos por Status
              </Heading>
              <Box mb={4}>
                <Table variant="simple" size="sm">
                  <Thead bg="#378BBF">
                    <Tr>
                      <Th color="white" borderBottom="none" borderRight="none">
                        Status
                      </Th>
                      <Th color="white" borderBottom="none" borderRight="none" isNumeric>
                        Quantidade
                      </Th>
                      <Th color="white" borderBottom="none" borderRight="none" isNumeric>
                        Valor Total
                      </Th>
                      <Th color="white" borderBottom="none" isNumeric>
                        % Total
                      </Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {estatisticas.porStatus.map((item, index) => (
                      <Tr key={item.status} bg={index % 2 === 1 ? 'gray.50' : 'white'}>
                        <Td borderBottom="none">
                          <Badge
                            colorScheme={
                              item.status.toLowerCase().includes('entregue')
                                ? 'green'
                                : item.status.toLowerCase().includes('cancelado')
                                  ? 'red'
                                  : item.status.toLowerCase().includes('andamento')
                                    ? 'blue'
                                    : item.status.toLowerCase().includes('produç')
                                      ? 'purple'
                                      : 'gray'
                            }
                          >
                            {item.status}
                          </Badge>
                        </Td>
                        <Td isNumeric borderBottom="none">
                          {item.quantidade}
                          <Text as="span" fontSize="xs" color="gray.500" ml={1}>
                            ({item.percentualQuantidade.toFixed(1)}%)
                          </Text>
                        </Td>
                        <Td isNumeric borderBottom="none">
                          {item.valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                        </Td>
                        <Td borderBottom="none">
                          <Flex justify="flex-end" align="center" gap={2}>
                            <Progress
                              value={item.percentualQuantidade}
                              size="xs"
                              colorScheme={
                                item.status.toLowerCase().includes('entregue')
                                  ? 'green'
                                  : item.status.toLowerCase().includes('cancelado')
                                    ? 'red'
                                    : item.status.toLowerCase().includes('andamento')
                                      ? 'blue'
                                      : item.status.toLowerCase().includes('produç')
                                        ? 'purple'
                                        : 'gray'
                              }
                              width="60px"
                              borderRadius="md"
                            />
                            <Badge>{item.percentualQuantidade.toFixed(1)}%</Badge>
                          </Flex>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>

              <Divider my={4} />

              <Heading size="sm" mb={2}>
                Pedidos por Fornecedor
              </Heading>
              <Box mb={4}>
                <Table variant="simple" size="sm">
                  <Thead bg="#378BBF">
                    <Tr>
                      <Th color="white" borderBottom="none" borderRight="none">
                        Fornecedor
                      </Th>
                      <Th color="white" borderBottom="none" borderRight="none" isNumeric>
                        Quantidade
                      </Th>
                      <Th color="white" borderBottom="none" borderRight="none" isNumeric>
                        Valor Total
                      </Th>
                      <Th color="white" borderBottom="none" isNumeric>
                        % Total
                      </Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {estatisticas.porFornecedor.map((item, index) => (
                      <Tr key={item.fornecedor} bg={index % 2 === 1 ? 'gray.50' : 'white'}>
                        <Td borderBottom="none">{item.fornecedor}</Td>
                        <Td isNumeric borderBottom="none">
                          {item.quantidade}
                          <Text as="span" fontSize="xs" color="gray.500" ml={1}>
                            ({item.percentualQuantidade.toFixed(1)}%)
                          </Text>
                        </Td>
                        <Td isNumeric borderBottom="none">
                          {item.valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                        </Td>
                        <Td borderBottom="none">
                          <Flex justify="flex-end" align="center" gap={2}>
                            <Progress value={item.percentualQuantidade} size="xs" colorScheme="blue" width="60px" borderRadius="md" />
                            <Badge colorScheme="blue">{item.percentualQuantidade.toFixed(1)}%</Badge>
                          </Flex>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>

              {estatisticas.porTransportadora.length > 1 && (
                <>
                  <Divider my={4} />
                  <Heading size="sm" mb={2}>
                    Pedidos por Transportadora
                  </Heading>
                  <Box mb={4}>
                    <Table variant="simple" size="sm">
                      <Thead bg="#378BBF">
                        <Tr>
                          <Th color="white" borderBottom="none" borderRight="none">
                            Transportadora
                          </Th>
                          <Th color="white" borderBottom="none" borderRight="none" isNumeric>
                            Quantidade
                          </Th>
                          <Th color="white" borderBottom="none" isNumeric>
                            % Total
                          </Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {estatisticas.porTransportadora.map((item, index) => (
                          <Tr key={item.transportadora} bg={index % 2 === 1 ? 'gray.50' : 'white'}>
                            <Td borderBottom="none">{item.transportadora}</Td>
                            <Td isNumeric borderBottom="none">
                              {item.quantidade}
                            </Td>
                            <Td borderBottom="none">
                              <Flex justify="flex-end" align="center" gap={2}>
                                <Progress value={item.percentual} size="xs" colorScheme="green" width="60px" borderRadius="md" />
                                <Badge colorScheme="green">{item.percentual.toFixed(1)}%</Badge>
                              </Flex>
                            </Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </Box>
                </>
              )}
            </>
          ) : (
            <Box textAlign="center" py={4}>
              <Text>Nenhum pedido disponível para mostrar.</Text>
            </Box>
          )}
        </ModalBody>

        <ModalFooter>
          <Button colorScheme="blue" mr={3} onClick={onClose}>
            Fechar
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
